import { getProjectDetailMiddleware } from "pages/projects/services/api"
import { defaultProjectDetail, ProjectDetail } from "pages/projects/types"
import { useEffect, useState } from "react"
import iconNoData from "assets/images/icons/icon-no-data-component.svg"
import { useBoolean, useString } from "helpers/hooks"
import Button from "components/Button/Button"
import {
  getUsersInProject,
  setRoleInProjectMiddleware,
} from "pages/project-component/services/api"
import {
  GetUsersInProjectProp,
  SetRoleInProjectProps,
} from "pages/project-component/types"
import { Avatar } from "components/Avatar"
import { ReactComponent as IconHasKey } from "assets/images/icons/icon-checked-build-active.svg"
import { ReactComponent as IconNotHasKey } from "assets/images/icons/icon-active-select-currency.svg"
import { EncryptionKeys } from "controllers/EncryptionController"
import { EncryptedDataWarning } from "components/EncryptedDataWarning"
import { useAppSelector } from "hooks/useApp"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { STATUS_RESPONSE } from "types"
import { cloneDeep } from "lodash"
import { SkeletonProjectDetail } from "../skeleton/SkeletonAccessKeyProject"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"

interface Props {
  projectIdActive: {
    setValue: (value: string) => void
    value: string
  }
  reloadPageProjectWhenShareAllKey: {
    setValue: (value: boolean) => void
    value: boolean
  }
}
export const ProjectInviteeCard = (props: Props) => {
  const { projectIdActive, reloadPageProjectWhenShareAllKey } = props
  const userInfo = useAppSelector((state) => state.userInfo)
  const projectError = useString()
  const [projectDetail, setProjectDetail] =
    useState<ProjectDetail>(defaultProjectDetail)
  const [usersInProject, setUsersInProject] = useState<GetUsersInProjectProp[]>(
    []
  )
  const projectEncryptionKeys = JSON.parse(
    localStorage.getItem(EncryptionKeys.projectEncryptionKeys) || "{}"
  )
  const currentProjectEncryptionKey =
    projectEncryptionKeys[projectIdActive.value]
  const loadingData = useBoolean(false)

  useEffect(() => {
    if (!projectIdActive.value) {
      setProjectDetail(defaultProjectDetail)
      return
    }
    getProjectDetail()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdActive.value])

  useEffect(() => {
    if (!reloadPageProjectWhenShareAllKey.value) {
      return
    }
    getProjectDetail()
    reloadPageProjectWhenShareAllKey.setValue(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadPageProjectWhenShareAllKey.value])

  const getProjectDetail = async () => {
    try {
      loadingData.setValue(true)
      setProjectDetail(defaultProjectDetail)
      setUsersInProject([])
      const dataProjectDetailRes = await getProjectDetailMiddleware(
        projectIdActive.value,
        undefined,
        undefined,
        true
      )
      if (dataProjectDetailRes?.id) {
        setProjectDetail(dataProjectDetailRes)
        const dataRes = await getUsersInProject(dataProjectDetailRes.id, true)
        setUsersInProject(dataRes)
      }
    } catch (error) {
      projectError.setValue("Project not found")
    } finally {
      loadingData.setValue(false)
    }
  }

  const setRoleInProjectAsync = (
    projectId: string,
    data: SetRoleInProjectProps,
    silent: boolean
  ): Promise<{ success: boolean; message: string }> => {
    return new Promise((resolve) => {
      setRoleInProjectMiddleware(
        projectId,
        data,
        silent,
        (type, messenger) => {
          resolve({
            success: type === STATUS_RESPONSE.SUCCESS,
            message: messenger,
          })
        },
        false
      )
    })
  }

  const handleWarningShareAllKey = (dataRequest: GetUsersInProjectProp[]) => {
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: "Are you sure you want to share all keys?",
            label: `Warning`,
            content: `This action cannot be undone!`,
            styleTitle: { textAlign: "center" },
            titleButton: `Proceed`,
            colorYellowButton: true,
            onSubmit: async () => {
              closeModal()
              handleShareKey(dataRequest)
            },
          },
        },
      })
    )
  }

  const handleShareKey = async (dataRequest: GetUsersInProjectProp[]) => {
    if (!dataRequest.length) {
      return
    }
    for (const elRequest of dataRequest) {
      const { success, message } = await setRoleInProjectAsync(
        projectIdActive.value,
        {
          email: elRequest.email,
          role: elRequest.role,
        },
        false
      )

      if (success) {
        setUsersInProject((prevUsers) => {
          const newUsers = cloneDeep(prevUsers)
          const index = newUsers.findIndex(
            (el) => el.user_id === elRequest.user_id
          )
          if (index > -1) {
            newUsers[index].has_key = true
          }
          return newUsers
        })
        toast(
          <LabelNotificationPage
            messenger={`The key has been successfully ${
              elRequest.has_key ? "re-shared" : "shared"
            } with ${elRequest.email}!`}
            type="success"
          />
        )
      } else {
        toast(
          <LabelNotificationPage
            messenger={`${elRequest.email}: ${message}`}
            type="error"
          />
        )
      }
    }
  }

  if (!currentProjectEncryptionKey && projectIdActive.value) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <EncryptedDataWarning
          type="project"
          isOwner={projectDetail.created_by === userInfo.id}
          name={`"${projectDetail.name}"`}
          id={projectDetail.id}
          isEncrypted={projectDetail.is_encrypted || false}
        />
      </div>
    )
  }
  if (loadingData.value) {
    return <SkeletonProjectDetail />
  }
  if (!projectDetail.id) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <img
          src={iconNoData}
          alt="icon"
          style={{
            height: 168,
          }}
        />
        {projectError.value ? (
          <p
            className="font-normal my-6"
            style={{
              fontSize: 14,
              lineHeight: "24px",
              color: "#111111",
            }}
          >
            {projectError.value}
          </p>
        ) : null}
      </div>
    )
  }
  return (
    <div className="flex flex-col h-full w-full">
      <div className="min-h-[74px] border-b border-[#E4E4E4] flex items-center justify-between pl-[24px] pr-[24px]">
        <div className="flex items-center">
          <p className="font-semibold text-[14px] leading-[24px] text-[#111111]">
            {projectDetail.name}
          </p>
        </div>
        {usersInProject.length ? (
          <Button
            title="Share all keys"
            onClick={() => handleWarningShareAllKey(usersInProject)}
            sizeBtn="small"
            colorBtn="yellow"
            isDisabledBtn
            heightBtn={35}
            widthBtn={120}
          />
        ) : null}
      </div>
      <div className="flex flex-col flex-1 overflow-auto p-[24px] gap-[12px]">
        {usersInProject.length ? (
          usersInProject.map((user, index) => {
            return (
              <div
                key={index}
                className="grid grid-cols-3 border border-[#E4E4E4] p-[12px] rounded-[6px]"
              >
                <div className="flex items-center">
                  <Avatar
                    width="24px"
                    height="24px"
                    name={
                      typeof user?.is_new_user === "boolean" &&
                      user?.is_new_user === false
                        ? "?"
                        : user?.fullname
                    }
                    avatar={user?.avatar}
                    textcolor="white"
                    contentsize="11px"
                    containerclass={
                      typeof user?.is_new_user === "boolean" &&
                      user?.is_new_user === false
                        ? "bg-gray-custom"
                        : undefined
                    }
                  />

                  <p className="font-semibold overflow-hidden text-ellipsis text-[#111111] text-[13px] ml-[12px] leading-[22px]">
                    {user?.fullname || "N/A"}
                  </p>
                  <div className="h-[4px] min-w-[4px] mx-2 bg-[#7A7A7A] rounded-full" />
                  <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                    {user.email}
                  </p>
                </div>
                <div className="flex items-center justify-center">
                  {user.has_key ? (
                    <IconHasKey width={20} height={20} />
                  ) : (
                    <IconNotHasKey />
                  )}
                  <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A] ml-[4px]">
                    Project key
                  </p>
                </div>
                <div className="flex items-center justify-end">
                  <Button
                    title={user.has_key ? "Re-share key" : "Share key"}
                    sizeBtn="small"
                    onClick={() => {
                      handleShareKey([user])
                    }}
                    colorBtn="white"
                    styleButton={{
                      minWidth: 100,
                    }}
                  />
                </div>
              </div>
            )
          })
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <img
              src={iconNoData}
              alt="icon"
              style={{
                height: 168,
              }}
            />
          </div>
        )}
      </div>
    </div>
  )
}
